import { useEffect, useState } from 'react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  Input,
  FormField,
  Modal,
  ModalBody,
  ModalFooter,
  NotificationCard,
  StatusBadge,
  useToast,
} from '@afrilink/ui';
import { CheckCircle2, XCircle, Download, FileText, Receipt } from 'lucide-react';
import {
  adminBillingService,
  type AdminProofRow,
} from '@/domains/billing/services/adminBillingService';
import { formatDateTime, formatZAR } from '@/shared/utils/format';

function formatBytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminProofsOfPaymentPage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<AdminProofRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [actioning, setActioning] = useState<{ row: AdminProofRow; decision: 'verify' | 'reject' } | null>(null);
  const [staffNote, setStaffNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  function reload() {
    setLoading(true);
    setError(null);
    adminBillingService
      .listPendingProofs()
      .then((data) => {
        setRows(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      });
  }

  useEffect(reload, []);

  async function openFile(row: AdminProofRow) {
    if (opening || !row.storage_path) return;
    setOpening(row.id);
    try {
      const url = await adminBillingService.getProofDownloadUrl(row.storage_path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast({ tone: 'danger', title: 'Could not open file', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setOpening(null);
    }
  }

  async function submit() {
    if (!actioning) return;
    setSubmitting(true);
    try {
      await adminBillingService.verifyProof(actioning.row.id, {
        decision: actioning.decision,
        staff_note: staffNote.trim() || undefined,
      });
      toast({
        tone: actioning.decision === 'verify' ? 'success' : 'warning',
        title: actioning.decision === 'verify' ? 'Proof verified — invoice marked paid' : 'Proof rejected',
        body: `${actioning.row.invoice_number} · ${actioning.row.customer_name ?? actioning.row.customer_email ?? ''}`,
      });
      setActioning(null);
      setStaffNote('');
      reload();
    } catch (e) {
      toast({ tone: 'danger', title: 'Action failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <UniversalPage
      title="Proofs of payment"
      breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: 'Billing', href: '/admin/billing' },
        { label: 'Proofs' },
      ]}
    >
      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Customers upload proofs of payment from their Billing page when paying via EFT. Verifying
          a proof <strong>marks the invoice paid + auto-unsuspends</strong> the linked cPanel
          account if applicable (per AD-15 per-package rule). The exact-amount enforcement is done
          server-side by <code>proof-of-payment-verify</code>; you cannot tell it the amount.
        </p>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load proofs" body={error.message} />}

      {loading && <UniversalLoadingState variant="spinner" caption="Loading pending proofs…" />}

      {!loading && rows.length === 0 && (
        <Card interactive={false} className="p-12 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-success" />
          <p className="text-sm font-medium text-foreground">Nothing to review</p>
          <p className="mt-1 text-xs text-muted-foreground">
            All uploaded proofs of payment have been verified or rejected.
          </p>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((r) => (
            <Card key={r.id} interactive={false}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <code className="text-sm font-semibold text-foreground">
                      {r.invoice_number}
                    </code>
                    <StatusBadge tone="pending">pending</StatusBadge>
                    {r.submitted_by_sales && (
                      <StatusBadge tone="info">EFT — raised by sales</StatusBadge>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-foreground">{r.customer_name ?? '—'}</p>
                  {r.customer_email && (
                    <p className="text-xs text-muted-foreground">{r.customer_email}</p>
                  )}
                  {r.submitted_by_sales && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Agent: {r.submitted_by_sales_email ?? '—'}
                      {r.submission_note ? ` · ${r.submission_note}` : ''}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Invoice total</p>
                  <p className="mt-1 text-lg font-semibold text-foreground">{formatZAR(r.invoice_total)}</p>
                </div>
                <div className="sm:col-span-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">
                    {r.submitted_by_sales ? 'Requested' : 'Uploaded'}
                  </p>
                  <p className="mt-1 text-sm text-foreground">{formatDateTime(r.uploaded_at)}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.storage_path
                      ? `${r.mime_type ?? '—'} · ${formatBytes(r.file_size_bytes)}`
                      : 'No file — verify against bank statement'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2 sm:col-span-2">
                  {r.storage_path && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openFile(r)}
                      loading={opening === r.id}
                      disabled={opening !== null}
                    >
                      <Download className="h-3.5 w-3.5" /> View
                    </Button>
                  )}
                </div>
              </div>
              <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-card-subtle px-4 py-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setActioning({ row: r, decision: 'reject' });
                    setStaffNote('');
                  }}
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    setActioning({ row: r, decision: 'verify' });
                    setStaffNote('');
                  }}
                >
                  <Receipt className="h-3.5 w-3.5" /> Verify &amp; mark paid
                </Button>
              </footer>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={actioning !== null}
        onOpenChange={(v) => !v && setActioning(null)}
        title={
          actioning?.decision === 'verify'
            ? `Verify ${actioning.row.invoice_number}?`
            : `Reject proof for ${actioning?.row.invoice_number ?? ''}?`
        }
        description={
          actioning?.decision === 'verify'
            ? `This marks the invoice paid for ${formatZAR(actioning.row.invoice_total)} and auto-unsuspends the linked cPanel account if applicable. The amount you record must equal the invoice total exactly (AD-13). Server-side check enforces this — if your bank statement shows a different amount, reject and ask the customer to redeposit.`
            : `Customer will be notified. You can include a note explaining why so they know what to do next.`
        }
        size="wide"
      >
        <ModalBody>
          <FormField
            label="Staff note (optional)"
            htmlFor="staff-note"
            helper={
              actioning?.decision === 'reject'
                ? 'Required-feeling: tell the customer what was wrong (wrong amount, illegible, missing reference, etc.).'
                : 'Internal note recorded on the invoice for future reference.'
            }
          >
            <Input
              id="staff-note"
              value={staffNote}
              onChange={(e) => setStaffNote(e.target.value)}
              disabled={submitting}
              placeholder={
                actioning?.decision === 'reject'
                  ? 'e.g. Amount paid R450 but invoice is R500 — please redeposit the difference.'
                  : 'e.g. Verified against FNB statement 2026-05-04.'
              }
            />
          </FormField>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setActioning(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            disabled={submitting}
          >
            {actioning?.decision === 'verify' ? 'Confirm verify' : 'Confirm reject'}
          </Button>
        </ModalFooter>
      </Modal>
    </UniversalPage>
  );
}
